<template>
  <div
    class="v2-product-card"
    :class="{
      'v2-product-card--compact': viewMode === 'compact',
      'v2-product-card--selection-mode': isSelectionMode,
      'v2-product-card--kp3462': isKp3462Layout,
    }"
  >
    <!-- Top-left badges -->
    <div class="v2-product-card__badges-left">
      <span
        v-if="product.productInfo.status === 'cancelled'"
        class="v2-product-card__cancelled"
      >
        Cancelled
      </span>
      <span
        v-if="showKeyStyle && product.productInfo.keyStyles"
        class="v2-product-card__key-style"
      >
        {{ product.productInfo.keyStyles }}
      </span>
    </div>

    <!-- Checkbox (top-left for brands, top-right for brands kept for compat) -->
    <v2-checkbox
      v-if="showCheckbox && product.productInfo.price !== 'N/A'"
      class="v2-product-card__checkbox"
      :class="{ 'v2-product-card__checkbox--visible': isChecked }"
      :value="isChecked"
      @input="$emit('check', { product, checked: $event })"
    />

    <!-- Clickable area (image + body) -->
    <div
      class="v2-product-card__clickable"
      @click="$emit('click', product)"
    >
      <!-- Image -->
      <div class="v2-product-card__image-wrap">
        <div class="v2-product-card__image">
          <img
            :src="parseProductImage(product.productInfo)"
            :alt="product.productInfo.name"
            class="v2-product-card__img"
            @error="onImageError"
          >
        </div>
        <!-- Qty badge (bottom-left of image) -->
        <div
          v-if="product.productInfo.quantityAvailable !== undefined && product.productInfo.quantityAvailable !== null"
          class="v2-product-card__qty-badge"
          :class="product.productInfo.quantityAvailable === 0 ? 'v2-product-card__qty-badge--oos' : ''"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="9"
              cy="21"
              r="1"
            />
            <circle
              cx="20"
              cy="21"
              r="1"
            />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          Qty ({{ product.productInfo.quantityAvailable }})
        </div>
        <!-- Color dots (outside overflow:hidden so tooltip is visible).
             Like the pre-V2 shop card (KP-2878): 5 dots and a "+N" that
             expands the column in place, "−" collapses it. Expanded, every
             colour is listed and past 10 the column scrolls. -->
        <div
          v-if="visibleColors.length"
          class="v2-product-card__colors"
          @click.stop
        >
          <button
            v-if="hiddenColorCount > 0 || isColorsExpanded"
            type="button"
            class="v2-product-card__color-toggle"
            :aria-expanded="String(isColorsExpanded)"
            :aria-label="isColorsExpanded ? 'Show fewer colours' : `Show ${hiddenColorCount} more colours`"
            @click="toggleColors"
          >
            <template v-if="isColorsExpanded">
              &minus;
            </template>
            <template v-else>
              +{{ hiddenColorCount }}
            </template>
          </button>
          <!-- Scroll colours up: shows the column scrolls; dimmed at that end -->
          <button
            v-if="colorScroll.scrollable"
            type="button"
            class="v2-product-card__color-scroll"
            :disabled="!colorScroll.up"
            aria-label="Scroll colours up"
            @click="scrollColorDots(-1)"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
          <div
            ref="colorDots"
            class="v2-product-card__color-dots"
            :class="{
              'v2-product-card__color-dots--expanded': isColorsExpanded,
              'v2-product-card__color-dots--more-above': colorScroll.scrollable && colorScroll.up,
              'v2-product-card__color-dots--more-below': colorScroll.scrollable && colorScroll.down,
            }"
            @mousemove="onColorDotsPointer"
            @mouseleave="hideColorName"
            @scroll="onColorDotsScroll"
          >
            <span
              v-for="(pc, index) in shownColors"
              :key="index"
              class="v2-product-card__color-dot"
              :style="'background:' + getColorCode(pc.colorFamily, pc.hexCode)"
              :aria-label="colorLabel(pc)"
            />
          </div>
          <!-- Scroll colours down: shows the column scrolls; dimmed at that end -->
          <button
            v-if="colorScroll.scrollable"
            type="button"
            class="v2-product-card__color-scroll"
            :disabled="!colorScroll.down"
            aria-label="Scroll colours down"
            @click="scrollColorDots(1)"
          >
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <!-- One name label outside the scroll box, which would clip it -->
          <span
            v-if="hoveredColor"
            class="v2-product-card__color-name"
            :style="{ top: `${hoveredColor.top}px` }"
          >{{ hoveredColor.label }}</span>
        </div>
      </div>

      <!-- Body -->
      <div class="v2-product-card__body">
        <div
          class="v2-product-card__name"
          :title="product.productInfo.name"
        >
          {{ product.productInfo.name }}
        </div>
        <div class="v2-product-card__style v2-text-xs v2-text-muted">
          Style Code: {{ product.productInfo.styleCode }}
        </div>
        <!-- KP-4350: segment tags (e.g. MCA) sit under the style code -->
        <v2-segment-tags
          :product="product.productInfo"
          class="v2-product-card__segment-tags"
        />
        <div class="v2-product-card__pricing">
          <div
            v-if="showWsp && hasWholesalePrice"
            class="v2-product-card__price-row"
          >
            <span class="v2-product-card__price-label">WSP:</span>
            <span
              v-if="!product.productInfo.discountedPrice"
              class="v2-product-card__price-value"
            >
              {{ formatCurrency(listWholesalePrice, currency || product.productInfo.currency) }}
            </span>
            <span
              v-if="product.productInfo.discountedPrice"
              class="v2-product-card__price-value v2-product-card__price-value--discounted"
            >
              {{ formatCurrency(product.productInfo.discountedPrice, currency || product.productInfo.currency) }}
            </span>
          </div>
          <div
            v-if="hasRetailPrice"
            class="v2-product-card__price-row"
          >
            <span class="v2-product-card__price-label">RRP:</span>
            <span class="v2-product-card__price-value v2-text-muted">
              {{ formatCurrency(product.productInfo.retailPrice, currency || product.productInfo.currency) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions + Footer in one row -->
    <div
      v-if="(showActions && actions.length) || $slots.footer"
      class="v2-product-card__footer-row"
    >
      <div
        v-if="showActions && actions.length"
        class="v2-product-card__actions"
      >
        <v2-button
          v-for="action in actions"
          :key="action.key"
          :variant="action.variant || 'ghost'"
          size="sm"
          :disabled="action.disabled"
          @click="$emit('action', { action: action.key, product })"
        >
          {{ action.label }}
        </v2-button>
      </div>
      <!-- Footer slot (e.g. selections dropdown) -->
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
import { getColorCode } from '@/product-colors'
import { getDefaultProductImage } from '@/@core/utils/utils'
import { normalizeImageUrl } from '@/utils/productImageUrl'
import { utils } from '@kingpin-global/kingpin-utils-frontend'
import { FLAG_CONSTANTS, MAX_COLORS_PER_PRODUCT_CARD } from '@/constants'
import { hasWholesalePriceFor, listWholesalePriceOf } from '@/utils/productPricing'
import V2Checkbox from '@/components/v2/V2Checkbox.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2SegmentTags from '@/components/v2/V2SegmentTags.vue'

const { formatCurrency } = utils

export default {
  name: 'V2ProductCard',
  components: {
    V2Checkbox,
    V2Button,
    V2SegmentTags,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    isChecked: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    actions: {
      type: Array,
      default: () => [],
    },
    showKeyStyle: {
      type: Boolean,
      default: true,
    },
    viewMode: {
      type: String,
      default: 'grid',
    },
    showWsp: {
      type: Boolean,
      default: true,
    },
    isSelectionMode: {
      type: Boolean,
      default: false,
    },
    currency: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      getColorCode,
      isColorsExpanded: false,
      hoveredColor: null,
      colorPointerY: null,
      colorScroll: { scrollable: false, up: false, down: false },
    }
  },
  computed: {
    // KP-3861: price rows render from what the payload actually carries.
    //
    // A wishlist can hide wholesale, retail or both, and the server strips the
    // hidden ones before they ever reach the client. Keying the row off the
    // data rather than off a visibility prop means every surface that consumes
    // a stripped payload — the public link, the shared copy, a retailer's own
    // view — is covered without threading the setting through each one. Without
    // this the row survived and rendered a formatted `undefined`.
    hasWholesalePrice() {
      return hasWholesalePriceFor(this.product.productInfo)
    },

    /** The figure shown when there is no discount — see wholesalePriceOf. */
    listWholesalePrice() {
      return listWholesalePriceOf(this.product.productInfo)
    },

    hasRetailPrice() {
      return typeof (this.product.productInfo || {}).retailPrice === 'number'
    },

    // Skip colours the map cannot resolve, same as ShopProducts: without a
    // background the dot renders as a blank white circle.
    visibleColors() {
      return (this.product.colors || []).filter(
        pc => getColorCode(pc.colorFamily, pc.hexCode) !== null,
      )
    },

    // Collapsed, the same 5 as ShopProducts; expanded, every colour (the
    // column scrolls past MAX_COLORS_EXPANDED).
    shownColors() {
      return this.isColorsExpanded
        ? this.visibleColors
        : this.visibleColors.slice(0, MAX_COLORS_PER_PRODUCT_CARD)
    },

    hiddenColorCount() {
      return this.visibleColors.length - this.shownColors.length
    },

    // KP-3462: portrait 3:4 image box (vs original 2:3) only when the
    // space-reclaiming layout flag is on.
    isKp3462Layout() {
      const hasFeature = this.$store?.getters?.['flags/hasFeature']
      return typeof hasFeature === 'function'
        ? hasFeature(FLAG_CONSTANTS.KP_3462_COLLAPSIBLE_SIDEBAR)
        : false
    },
  },
  methods: {
    formatCurrency,
    // The wishlist composable emits colorNames[] and no `color` field.
    colorLabel(pc) {
      return pc.color || (pc.colorNames || []).join(', ')
    },
    onColorDotsPointer(event) {
      this.colorPointerY = event.clientY
      this.showColorNameAt(event.clientY)
    },
    // Scrolling slides other dots under a still pointer without any mouse
    // event, so the name is looked up again at the last pointer height.
    onColorDotsScroll() {
      this.updateColorScrollHints()
      if (this.colorPointerY !== null) this.showColorNameAt(this.colorPointerY)
    },
    toggleColors() {
      this.isColorsExpanded = !this.isColorsExpanded
      this.hideColorName()
      this.$nextTick(this.updateColorScrollHints)
    },
    // Whether the expanded column scrolls, and which ways it still can; the
    // arrows and edge fades are drawn from this.
    updateColorScrollHints() {
      const box = this.$refs.colorDots
      if (!box) return
      const max = box.scrollHeight - box.clientHeight
      this.colorScroll = {
        scrollable: this.isColorsExpanded && max > 1,
        up: box.scrollTop > 1,
        down: box.scrollTop < max - 1,
      }
    },
    scrollColorDots(direction) {
      const box = this.$refs.colorDots
      box.scrollBy({ top: direction * box.clientHeight * 0.6, behavior: 'smooth' })
    },
    // Names the dot nearest the pointer's height, so the 2px gaps between
    // dots keep a name instead of flickering, and centres the label on it.
    // The dot's offsetTop is measured from the colours column, so the dots'
    // scroll offset is taken off.
    showColorNameAt(y) {
      const box = this.$refs.colorDots
      const dots = Array.from(box.children)
      const distance = dot => {
        const { top, height } = dot.getBoundingClientRect()
        return Math.abs(top + height / 2 - y)
      }
      const dot = dots.reduce((best, d) => (distance(d) < distance(best) ? d : best), dots[0])
      this.hoveredColor = {
        label: this.colorLabel(this.shownColors[dots.indexOf(dot)]),
        top: dot.offsetTop - box.scrollTop + dot.offsetHeight / 2,
      }
    },
    hideColorName() {
      this.hoveredColor = null
      this.colorPointerY = null
    },
    onImageError(event) {
      event.target.src = getDefaultProductImage()
    },
    parseProductImage(product) {
      if (product?.thumbnail) {
        return normalizeImageUrl(product.thumbnail)
      }
      if (product?.images?.length && product.images[0]) {
        return normalizeImageUrl(product.images[0])
      }
      return getDefaultProductImage()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-product-card {
  // Canonical no-backdrop-filter glass — same recipe as V2Card panel
  // variant. Card grids render 50+ cards at once; per-card blur would
  // overwhelm the GPU. The mixin layers a sheen gradient + glass-stroke
  // + compound shadow over the translucent fill; the page mesh
  // substrate (LayoutV2::before) provides the optical "frosted" effect
  // once for the whole viewport. See docs/glassmorphism-migration-plan.md note #3.
  @include v2-glass-panel-flat;
  position: relative;
  // contain: paint lets the browser skip painting work on cards outside
  // the viewport during scroll — significantly cheaper than blur but only
  // active when paint is contained to the card box (which it is here).
  // `contain: layout` only — adding `paint` would clip descendants to
  // the card box, hiding the V2ProductWishlistsDropdown menu (which
  // extends below the card). `layout` still gives the scroll-perf benefit.
  contain: layout;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: var(--v2-glass-stroke-strong);
    transform: translateY(-1px);
  }

  // Lift this card's stacking context above sibling cards while a
  // descendant popover is open (V2ProductWishlistsDropdown menu renders
  // only when isOpen=true, so :has matches dynamically). Each card has
  // backdrop-filter, which creates its own stacking context — without
  // this, the menu's z-index is bounded by the card and gets overlaid
  // by neighbouring cards in document order.
  &:has(.v2-wishlists-dropdown__menu) {
    z-index: 10;
  }

  &__badges-left {
    position: absolute;
    top: var(--v2-space-2);
    right: var(--v2-space-2);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--v2-space-1);
    z-index: 3;
  }

  &__cancelled {
    background: var(--v2-destructive);
    color: #fff;
    padding: var(--v2-space-0-5) var(--v2-space-2);
    border-radius: var(--v2-radius-sm);
    font-size: 0.625rem;
    font-weight: 600;
    width: fit-content;
  }

  &__key-style {
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    padding: var(--v2-space-0-5) var(--v2-space-2);
    border-radius: var(--v2-radius-sm);
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: capitalize;
  }

  &__checkbox {
    position: absolute;
    top: var(--v2-space-2);
    left: var(--v2-space-2);
    opacity: 0;
    transition: opacity 0.15s ease;
    z-index: 4;

    ::v-deep .v2-checkbox__box {
      width: 1.75rem;
      height: 1.75rem;
    }

    &--visible {
      opacity: 1;
    }
  }

  &:hover &__checkbox {
    opacity: 1;
  }

  &__qty-badge {
    position: absolute;
    bottom: var(--v2-space-2);
    left: var(--v2-space-2);
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: var(--v2-space-1) var(--v2-space-2);
    background: var(--v2-secondary);
    color: var(--v2-secondary-foreground);
    border-radius: var(--v2-radius-full);
    font-size: 0.75rem;
    font-weight: 600;
    z-index: 3;

    &--oos {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }
  }

  &__clickable {
    cursor: pointer;
  }

  &--selection-mode {
    // Checkbox stays visible at all times so users can deselect/select
    .v2-product-card__checkbox {
      opacity: 1;
    }

    // Disable everything inside the card while in selection mode.
    // Only the checkbox stays interactive so users can deselect/select.
    .v2-product-card__clickable,
    .v2-product-card__actions,
    .v2-product-card__footer-row {
      pointer-events: none;
    }
  }

  &__image-wrap {
    position: relative;
  }

  &__image {
    aspect-ratio: 2/3;
    overflow: hidden;
    background: white;
    border-top-left-radius: var(--v2-radius-lg);
    border-top-right-radius: var(--v2-radius-lg);
  }

  // KP-3462: shorter 3:4 portrait box so two rows fit in the denser grid. The
  // 2:3 default above was taller than most product shots and left top/bottom
  // gaps. `object-fit: contain` on &__img keeps every upload uncropped.
  &--kp3462 &__image {
    aspect-ratio: 3/4;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }

  &__colors {
    position: absolute;
    bottom: var(--v2-space-2);
    right: var(--v2-space-2);
    display: flex;
    flex-direction: column;
    align-items: center;
    // Stops below the top-right badges (Cancelled + key style, ~48px), so
    // an expanded column never covers them.
    max-height: calc(100% - var(--v2-space-2) - 48px);
    z-index: 3;
  }

  &__color-dots {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    min-height: 0;
  }

  // Room for 10 dots of 12px + 2px gap, then a thin scroll; on a compact
  // card the image height caps it sooner.
  &__color-dots--expanded {
    max-height: calc(10 * 14px);
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1px 2px;
    scrollbar-width: thin;
    scrollbar-color: var(--v2-glass-stroke-strong) transparent;
  }

  // Dots fade out at an edge the column can still scroll past, so the cut
  // reads as "more this way" rather than the end of the list.
  &__color-dots--more-above {
    mask-image: linear-gradient(to bottom, transparent 0, #000 18px);
  }

  &__color-dots--more-below {
    mask-image: linear-gradient(to top, transparent 0, #000 18px);
  }

  &__color-dots--more-above#{&}__color-dots--more-below {
    mask-image: linear-gradient(to bottom, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%);
  }

  // ▲ / ▼ around the expanded column: they show that it scrolls and scroll
  // it on click, dimmed at that end.
  &__color-scroll {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 14px;
    margin: 1px 0;
    padding: 0;
    color: var(--v2-text-muted-secondary);
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke-strong);
    border-radius: var(--v2-radius-full);
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

    &:hover:not(:disabled),
    &:focus-visible {
      color: var(--v2-text-primary);
      border-color: var(--v2-primary);
      outline: none;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }

  // The shop card's round "+N" / "−" button, in V2 tokens. It floats over
  // the product image, so it takes the floating surface.
  &__color-toggle {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 3px;
    margin-bottom: 2px;
    font-size: 0.625rem;
    font-weight: 600;
    line-height: 1;
    color: var(--v2-text-muted-secondary);
    background: var(--v2-glass-surface-floating);
    border: 1px solid var(--v2-glass-stroke-strong);
    border-radius: var(--v2-radius-full);
    cursor: pointer;
    transition: color 0.15s ease, border-color 0.15s ease;

    &:hover,
    &:focus-visible {
      color: var(--v2-text-primary);
      border-color: var(--v2-primary);
      outline: none;
    }
  }

  &__color-dot {
    // Fixed size, so a long expanded list scrolls instead of squashing.
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid var(--v2-border);
  }

  // The hovered dot's name, to its left; the arrow points at the dot.
  &__color-name {
    position: absolute;
    right: calc(100% + 8px);
    transform: translateY(-50%);
    background: var(--v2-foreground);
    color: #fff;
    font-size: 0.625rem;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    z-index: 100;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 100%;
      transform: translateY(-50%);
      border: 4px solid transparent;
      border-left-color: var(--v2-foreground);
    }
  }

  &__body {
    padding: var(--v2-space-3);
  }

  &__name {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__style {
    margin-top: var(--v2-space-1);
  }

  &__segment-tags {
    margin-top: var(--v2-space-1);
  }

  &__pricing {
    margin-top: var(--v2-space-2);
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
  }

  &__price-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    font-size: 0.75rem;
  }

  &__price-label {
    color: var(--v2-muted-foreground);
    font-weight: 500;
  }

  &__price-value {
    color: var(--v2-foreground);
    font-weight: 500;

    &--discounted {
      color: #10b981; // Green color for discounted price
    }
  }

  &__stock-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.125rem 0.375rem;
    border-radius: var(--v2-radius-sm);
    font-size: 0.625rem;
    font-weight: 600;
    margin-left: auto;

    svg {
      width: 13px;
      height: 13px;
    }

    &--oos {
      background: rgba(239, 68, 68, 0.1);
      color: #ef4444;
    }

    &--in-stock {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }
  }

  &__footer-row {
    display: flex;
    border-top: 1px solid var(--v2-border);
    min-height: 42px;
  }

  &__actions {
    display: flex;
    flex: 1;
    min-height: 42px;

    // When the wishlist toggle sits beside the actions, give the cart action
    // ~70% of the footer row so the wishlist toggle stays compact at ~30%.
    &:not(:only-child) {
      flex: 0 0 70%;
    }

    .v2-btn {
      flex: 1;
      border-radius: 0;
      border: none;
      min-height: 42px;

      &:first-child {
        border-bottom-left-radius: var(--v2-radius-lg);
      }

      + .v2-btn {
        border-left: 1px solid var(--v2-border);
      }
    }
  }

  // Footer slot (for selections dropdown) — sits beside actions in footer-row
  ::v-deep .v2-wishlists-dropdown {
    flex: 1;
    position: static;

    .v2-wishlists-dropdown__toggle {
      border-top: none;
      border-left: 1px solid var(--v2-border);
      border-bottom-right-radius: var(--v2-radius-lg);
    }

    // When actions sit beside the wishlist toggle (retailer card with Add/View in
    // Cart), shrink the toggle to ~30% so the cart action takes ~70%.
    // The dropdown menu still spans the full card width because the wishlists-dropdown
    // is `position: static` and the menu's left/right anchor to .v2-product-card.
    &:not(:only-child) {
      flex: 0 0 30%;
    }

    &:not(:only-child) .v2-wishlists-dropdown__toggle {
      padding: var(--v2-space-2) var(--v2-space-1);
      justify-content: center;
    }

    &:not(:only-child) .v2-wishlists-dropdown__toggle-icons {
      gap: var(--v2-space-1);
    }

    &:not(:only-child) .v2-wishlists-dropdown__heart {
      margin-left: 0;
    }

    .v2-wishlists-dropdown__menu {
      left: 0;
      right: 0;
      width: 100%;
    }
  }
}

.v2-text-muted { color: var(--v2-muted-foreground); }
</style>
